import { IVButtonDropDownItem } from "@core/v-button/v-button.component";
import { SortDirection } from "@enums/SortDirection";
import { IPlot, IPlotTemperatureRecord } from "@interfaces/IPlot";
import { IVehicle } from "@interfaces/IVehicle";
import { IVehicleInput } from "@interfaces/IVehicleInput";
import { ChartService, ChartType } from "@services/charts.service";
import { ColourService } from "@services/colour.service";
import { TranslationService } from "@services/translation.service";
import { UnitConversionService, UnitConversionType } from "@services/unitConversion.service";
import { VehicleService } from "@services/vehicle.service";
import { IVehicleInputRecordHistory, VehicleInputService } from "@services/vehicleInput.service";
import { VeriDate } from "@services/veriDate.service";
import { TemperatureProfileMaterialisationOptions } from "@temperature/_enums/temperature/TemperatureProfileMaterialisationOptions.enum";
import { IInputCategories } from "@temperature/_models/temperature/IInputCategories";
import { IProfileQueryOptions } from "@temperature/_models/temperature/IProfileQueryOptions";
import { ITemperatureProfile } from "@temperature/_models/temperature/ITemperatureProfile";
import { TemperatureService } from "@temperature/_services/temperature.service";
import * as angular from "angular";
import "./temperatureGraph.scss";



class TemperatureGraphComponentController implements angular.IComponentController {
    vehicle: IVehicle;
    start: Date;
    end: Date;

    loading = false;
    probeGraphLoading = false;
    inputChartLoading = false;

    showTooltip: boolean;
    showInputChart: boolean = true;
    legendTopRight: boolean;
    showResetZoom: boolean;

    tooltipElement: any;

    tooltipDate: string;
    lastUpdated: string;

    tooltipLeft: number;
    tooltipTop: number;
    tooltipWidth: number = 220;

    profileUpper: number;
    profileLower: number;

    plotlinePosition: number;

    tempProfiles: ITemperatureProfile[] = [];

    tooltipInputPoints: any[] = [];
    tooltipProbePoints: any[] = [];
    inputCategories: IInputCategories[] = [];

    plots: IPlot[];

    probeGraphConfig: Highcharts.Options;
    inputChartConfig: Highcharts.Options;

    inputChart: Highcharts.ChartObject;
    probeGraph: Highcharts.ChartObject;

    exporting: boolean = false;
    exportTypes: IVButtonDropDownItem<Highcharts.ChartObject>[];

    onPlotPointHover: (event: { $plotPoint: IPlot, $mouseXPos: number }) => void;

    colours = ["#0d3c55", "#0f5b78", "#117899", "#1395ba", "#5ca793", "#a2b86c", "#ebc844", "#ecaa38", "#ef8b2c", "#f16c20", "#d94e1f", "#c02e1d", "#c01d4e", "#b4189a", "#e42ed4", "#aa47dd"];

    static $inject = ["$scope", "$element", "$q", "$timeout", "vehicleService", "chartService", "vehicleInputService", "colourService", "veriDate", "temperatureService", "unitConversionService", "translationService", "veriDate"];

    constructor(
        private readonly $scope: angular.IScope,
        private readonly $element: JQLite,
        private readonly $q: angular.IQService,
        private readonly $timeout: angular.ITimeoutService,
        private readonly vehicleService: VehicleService,
        private readonly chartService: ChartService,
        private readonly vehicleInputService: VehicleInputService,
        private readonly colourService: ColourService,
        private readonly veriDateService: VeriDate,
        private readonly temperatureService: TemperatureService,
        private readonly unitConversionService: UnitConversionService,
        private readonly translationService: TranslationService,
        private readonly veriDate: VeriDate
    ) {
    }

    $onChanges(onChangesObj: angular.IOnChangesObject) {
        if (onChangesObj["vehicle"] || onChangesObj["start"] || onChangesObj["end"]) {
            this.resetState();

            this.tooltipElement = null;
            this.loadGraphData();
        }
    }

    private resetState() {
        this.exportTypes = [];

        delete this.probeGraph;
        delete this.inputChart;
        delete this.probeGraphConfig;
        delete this.inputChartConfig;
    }

    tooltipMovement($event: MouseEvent) {
        if (!this.probeGraphConfig) {
            return;
        }

        // min margin of yaxis label
        const yAxisMinLabelPadding = 80;

        //series label padding
        const seriesLabelPadding = this.probeGraphConfig.series[0]?.data ? 45 : 10;

        // min margin of xaxis label
        const XAxisLabelPadding = 41;

        if ($event.offsetX < yAxisMinLabelPadding || $event.offsetY <= seriesLabelPadding || this.tooltipProbePoints.length == 0 && this.tooltipInputPoints.length == 0) {
            this.hideTooltip();
        }
        else {
            this.showTooltip = true;
        }

        const tooltipContainer = $event.currentTarget as HTMLElement;
        if (!this.tooltipElement?.length) {
            this.tooltipElement = this.$element[0].getElementsByClassName('tooltip');
        }

        if (this.tooltipElement[0]) {
            this.tooltipLeft = $event.offsetX;
            this.tooltipLeft = Math.min(this.tooltipLeft, tooltipContainer.offsetWidth - this.tooltipWidth);

            const tooltipYOffset = 30;
            this.tooltipTop = $event.clientY - tooltipContainer.getBoundingClientRect().top - tooltipYOffset;
            this.tooltipTop = Math.min(this.tooltipTop, tooltipContainer.getBoundingClientRect().height - (this.tooltipElement[0].getBoundingClientRect().height + XAxisLabelPadding));
        }
    }

    hideTooltip() {
        this.showTooltip = false;
    }

    setPlotline($event: MouseEvent) {
        const charts = [this.inputChart, this.probeGraph];
        charts.forEach(currentchart => {
            if (!currentchart || !currentchart.xAxis) {
                return;
            }

            const xaxis = currentchart.xAxis[0] as any;

            this.plotlinePosition = xaxis.toValue($event.offsetX, false);

            this.chartService.addOrUpdateChartCrossHairFromMouseX(xaxis, $event.offsetX);
            this.tooltipDate = this.veriDateService.formatDate(new Date(this.plotlinePosition));
            this.updateTooltipData(currentchart, this.plotlinePosition);

            this.DisplayPlotOnMap(this.plotlinePosition);
        });
    }

    resetZoom() {
        const charts = [this.inputChart, this.probeGraph];

        charts.forEach(currentchart => {
            if (!currentchart || !currentchart.xAxis) {
                return;
            }

            currentchart.xAxis[0].setExtremes(null, null);

            this.showResetZoom = false;
        });
    }

    //-----------

    private displayTempProfilesOnGraph() {

        this.tempProfiles.forEach((profile, index) => {
            const yaxis = this.probeGraphConfig.yAxis as Highcharts.AxisOptions;

            yaxis.plotBands = yaxis.plotBands || [];
            yaxis.plotLines = yaxis.plotLines || [];

            const seriesColour = this.colourService.getVigetColor(index, 4, 3);

            yaxis.plotBands.push({
                from: profile.LowerBound,
                to: profile.UpperBound,
                id: profile.Name,
                color: this.colourService.createTransparentColor(seriesColour, 0.08),
            });

            yaxis.plotLines.push({
                value: profile.LowerBound,
                color: 'rgba(145,145,145, 0.4)',
                dashStyle: 'longdash',
                width: 1,
                zIndex: 3,
                label: {
                    text: `${profile.Name} Min`,
                    style: {
                        color: '#919191',
                        fontSize: "12",
                        fontWeight: "400"
                    }
                }
            });

            yaxis.plotLines.push({
                value: profile.UpperBound,
                color: 'rgba(145,145,145, 0.4)',
                dashStyle: 'longdash',
                width: 1,
                zIndex: 3,
                label: {
                    text: `${profile.Name} Max`,
                    y: 15,
                    style: {
                        color: '#919191',
                        fontSize: "12",
                        fontWeight: "400"
                    }
                }
            });
        });
    }

    private createInputChart(vehicleInputList: IVehicleInput[], inputHistoryList: IVehicleInputRecordHistory[]) {

        if (!inputHistoryList.length) {
            return;
        }

        const categories: string[] = [];
        const xRangeSeries: any[] = [];
        const seriesData: any[] = [];
        const seriesColour: string[] = [];

        vehicleInputList.forEach((vehicleInput, index) => {
            this.inputCategories = [];

            this.inputCategories.push({
                name: vehicleInput.name,
                inputStateDescription: vehicleInput.InputStateDescription,
                isMultiStateInput: false
            });

            categories.push(vehicleInput.name);

            seriesColour.push(this.colourService.getVigetColor(index, inputHistoryList.length, 7, this.colours));

            inputHistoryList.forEach(historyListItem => {
                if (vehicleInput.Id == historyListItem.InputId) {
                    historyListItem.inputName = vehicleInput.name;
                }

                if (!historyListItem.inputName) {
                    return;
                }

                let x: number;
                let x2 = historyListItem.EndUtc ? new Date(historyListItem.EndUtc).valueOf() : Date.now();

                if (this.veriDateService.date1IsBeforeDate2(historyListItem.StartUtc, this.start)) {
                    x = this.veriDateService.createMoment(this.start).valueOf();
                }
                else {
                    x = this.veriDateService.createMoment(historyListItem.StartUtc).valueOf();
                }

                if (this.veriDateService.date1IsAfterDate2(historyListItem.EndUtc, this.end)) {
                    x2 = this.veriDateService.createMoment(this.end).valueOf();
                }

                seriesData.push({
                    x: x,
                    x2: x2,
                    y: categories.indexOf(`${historyListItem.inputName}`),
                    name: `${historyListItem.inputName}`,
                    custom: {
                        inputStateDescription: vehicleInput.InputStateDescription
                    }
                });
            });
        });

        xRangeSeries.push({
            name: `${this.vehicle.Reg}`,
            type: 'xrange',
            pointWidth: 20,
            pointPadding: 0.4,
            data: seriesData,
            showInLegend: false
        });

        this.inputChartConfig = this.chartService.createBasicXRangeChart(xRangeSeries);
        this.inputChartConfig.exporting = this.getExportingOptions();

        this.chartService.setChartXAxisExtremes(this.inputChartConfig, Number(this.end), Number(this.start));

        const yAxis = this.inputChartConfig.yAxis as Highcharts.AxisOptions;

        yAxis.categories = [...categories];
        yAxis.minPadding = 100;

        this.inputChartConfig.chart.marginLeft = 90;
        this.inputChartConfig.colors = seriesColour;

        this.chartService.setOnLoadCallback(this.inputChartConfig, (chart) => {
            this.inputChart = chart;

            this.exportTypes.push({ id: 1, label: this.translationService.t("Common.Inputs"), icon: "panorama", data: this.inputChart });
            this.sortOutExportLabels();

            this.$timeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 1000);
        });

        this.inputChartConfig.chart.resetZoomButton = { position: { y: -100 } }; // hides reset button on the chart

        this.syncChartZooming(this.inputChartConfig);
    }


    private createProbeChart(records: IPlotTemperatureRecord[]) {
        if (!records.length) {
            return;
        }

        records.sort((a, b) => {
            return this.veriDateService.sort(a.Timestamp, b.Timestamp, SortDirection.Ascending);
        });

        const temperatureSeries: any[] = [];

        this.vehicle.probes.forEach((probe, index) => {

            const foundRecords = [];

            records.forEach(record => {
                if (record.Position === probe.Position && record.Source == probe.Source) {
                    foundRecords.push([new Date(record.Timestamp).valueOf(), this.unitConversionService.convertTemperature(record.Temperature)]);
                }
            });

            const seriesColour = this.colourService.getVigetColor(index, 16, 3, this.colours);

            if (foundRecords.length > 0) {
                temperatureSeries.push(
                    {
                        name: probe.cleanName,
                        data: foundRecords,
                        id: probe.Id,
                        zoneAxis: 'y',
                        color: `${seriesColour}`,
                        lineWidth: 1,
                        zones: [
                            {
                                value: probe.MinPrimaryThreshold,
                                dashStyle: 'ShortDot',
                                //color: "#005ab3"
                                color: seriesColour,
                            },
                            {
                                value: probe.MaxPrimaryThreshold,
                                dashStyle: 'solid',
                                color: seriesColour,
                            },
                            {
                                dashStyle: 'ShortDot',
                                //color: this.colourService.redColor
                                color: seriesColour,
                            }
                        ]
                    }
                );
            }
        });

        temperatureSeries.sort((a, b) => {
            //Ensure legend labels appear alphabetically
            if (a.name < b.name) {
                return -1;
            }
        })

        this.probeGraphConfig = this.createTempChartConfig("line", temperatureSeries, chart => {
            this.probeGraph = chart;

            this.exportTypes.push({ id: 0, label: this.translationService.t("Common.Temperature"), icon: "panorama", data: this.probeGraph });
            this.sortOutExportLabels();

            this.$timeout(() => {
                window.dispatchEvent(new Event('resize'));
            });
        });

        this.probeGraphConfig.exporting = this.getExportingOptions();

        this.chartService.setChartXAxisExtremes(this.probeGraphConfig, Number(this.end), Number(this.start));

        const series = this.probeGraphConfig.plotOptions as any;

        series.series = {
            states: {
                inactive: {
                    opacity: 1
                }
            },
            marker: {
                enabled: false,
                states: {
                    hover: {
                        enabled: false
                    }
                }
            }
        };

        this.probeGraphConfig.chart.marginLeft = 90;

        this.probeGraphConfig.chart.spacing = [10, 10, 10, 10];

        this.probeGraphConfig.chart.plotBackgroundColor = 'rgba(251,251,251,0.8)';

        this.probeGraphConfig.yAxis = {
            title: {
                text: `${this.translationService.t('Common.Temperature')} (${this.unitConversionService.getConversionLabel(UnitConversionType.Temperature)})`,
                margin: 20
            }
        };

        this.displayTempProfilesOnGraph();

        this.probeGraphConfig.chart.resetZoomButton = { position: { y: -100 } }; // hides reset button on the chart

        this.probeGraphConfig.tooltip = { enabled: false, shared: false };

        this.probeGraphConfig.legend = {
            enabled: true,
            layout: 'horizontal',
            align: 'right',
            verticalAlign: 'top',
            itemMarginTop: 0,
            itemMarginBottom: 0
        }

        this.syncChartZooming(this.probeGraphConfig);
    }

    private DisplayPlotOnMap(graphPlotLine: number) {
        if (!this.plots || !graphPlotLine) {
            return;
        }

        for (let i = 0; i < this.plots.length; i++) {

            if (!this.plots[i]) {
                return;
            }

            const plot = this.plots[i];

            if (this.tooltipDate == this.veriDateService.formatDate(plot.Utc)) {

                this.$scope.$applyAsync(() => {
                    this.onPlotPointHover({ $plotPoint: plot, $mouseXPos: graphPlotLine });
                });
            }
        }
    }

    private createTempChartConfig(chartType: ChartType, series: Highcharts.IndividualSeriesOptions[], callback: (chartObject: Highcharts.ChartObject, event: MouseEvent) => void): Highcharts.Options {
        const options = this.chartService.createChartOptions(chartType);
        options.series = series;
        options.plotOptions.line.states.hover.lineWidth = 3;

        this.chartService.setOnLoadCallback(options, callback);

        return options;
    }

    private doesLatestRequested(): boolean {
        const now = new Date();
        return this.veriDateService.date1IsBeforeDate2(now, this.end)
    }

    private loadGraphData(invalidateCache = false) {

        if (this.doesLatestRequested()) {
            invalidateCache = true;
        }

        this.probeGraphLoading = true;
        this.inputChartLoading = true;
        this.tempProfiles = [];

        if (!this.vehicle) {
            return;
        }

        const vehicleInputPromise = this.vehicleInputService.getVehicleInputs(this.vehicle.Id, invalidateCache);
        const inputHistoryPromise = this.vehicleInputService.getVehicleInputHistory(this.vehicle.Id, this.start, this.end, true, invalidateCache);
        const tempRecordPromise = this.vehicleService.getVehicleTemperatureRecords(this.vehicle.Id, this.veriDateService.createMoment(this.start), this.veriDateService.createMoment(this.end), invalidateCache);
        const queryOptions: IProfileQueryOptions = {
            MaterialisationOptions: TemperatureProfileMaterialisationOptions.WithHistories
        }
        const profilePromise = this.temperatureService.getTemperatureProfiles(invalidateCache, queryOptions);

        this.$q.all([vehicleInputPromise, inputHistoryPromise, tempRecordPromise, profilePromise])
            .then(([vehicleInputList, inputHistoryList, records, profiles]) => {
                this.vehicle.probes.forEach(probe => {

                    const profile = profiles.find(profile => probe.TemperatureProfileId === profile.Id);

                    if (!profile) {
                        return;
                    }

                    let relevantProfile = profile.TemperatureProfileHistories.find(history => {
                        //Find relevant profile data for the time period that is being viewed
                        return this.veriDate.createMoment(this.start).isBetween(this.veriDate.createMoment(history.Start), this.veriDate.createMoment(history.End));
                    }) as ITemperatureProfile;
                    if (!relevantProfile) {
                        relevantProfile = profile;
                    }
                    relevantProfile.LowerBound = this.unitConversionService.convertTemperature(relevantProfile.LowerBound);
                    relevantProfile.UpperBound = this.unitConversionService.convertTemperature(relevantProfile.UpperBound);
                    relevantProfile.Name = profile.Name;

                    probe.MinPrimaryThreshold = relevantProfile.LowerBound;
                    probe.MaxPrimaryThreshold = relevantProfile.UpperBound;

                    // If not added already, add to our list
                    if (!this.tempProfiles.find(p => p.Id == relevantProfile.Id)) {
                        this.tempProfiles.push(relevantProfile);
                    }
                });
                this.createProbeChart(records);
                this.createInputChart(vehicleInputList, inputHistoryList);
            })
            .finally(() => {
                this.showInputChart = this.inputChartConfig && this.inputChartConfig.series[0].data.length > 0;
                this.inputChartLoading = false;
                this.probeGraphLoading = false;
                if (invalidateCache) {
                    this.lastUpdated = new Date().toString();
                } else {
                    this.lastUpdated = null;
                }
            });
    }

    private syncChartZooming(currentChart: Highcharts.Options) {
        this.showResetZoom = false;
        const xAxis = currentChart.xAxis as Highcharts.AxisOptions;

        xAxis.startOnTick = true;

        xAxis.events = {
            afterSetExtremes: (event: any) => {
                if (event.userMax) {
                    this.showResetZoom = true;
                }

                if (event.trigger == "zoom") {
                    if (this.inputChart && currentChart == this.probeGraphConfig) {
                        this.inputChart.xAxis[0].setExtremes(event.min, event.max, true, true);
                    }

                    if (currentChart == this.inputChartConfig) {
                        this.probeGraph.xAxis[0].setExtremes(event.min, event.max, true, true);
                    }
                }
            }
        };

    }

    private updateTooltipData(chart: Highcharts.ChartObject, axisPoint: number) {
        const xaxis = chart.xAxis[0] as any;

        if (chart == this.probeGraph) {
            this.tooltipProbePoints = [];

            xaxis.series.forEach(seriesItem => {
                const foundProbePoint = seriesItem.data.find(p => p && p.x >= axisPoint);

                if (foundProbePoint) {
                    this.tooltipProbePoints.push(foundProbePoint);
                }
            });
        }

        if (chart == this.inputChart) {
            this.tooltipInputPoints = [];

            xaxis.series.forEach(seriesItem => {
                seriesItem.data.forEach(point => {
                    if (axisPoint >= point.x && axisPoint <= point.x2) {
                        this.tooltipInputPoints.push(point);
                    }
                });
            });
        }
    }

    sortOutExportLabels() {
        this.exportTypes.sort((a, b) =>
            a.label < b.label ? -1 : a.label > b.label ? 1 : 0
        ).reverse();  // reversed to match the order of the charts (1 temp. 2 inputs)
    }

    private getExportingOptions(): Highcharts.ExportingOptions {
        return {
            enabled: true,
            buttons: null,
            chartOptions: {
                title: {
                    text: this.vehicle.Reg
                },
                chart: {
                    backgroundColor: 'white'
                }
            }
        }
    }

    onItemClick(event: IVButtonDropDownItem<Highcharts.ChartObject>) {
        this.exporting = true;

        const filename = `${this.vehicle.Reg} ${event.label} ${this.veriDateService.formatDate(new Date(), 'Date')}`;

        event.data.exportChart({ filename });

        this.resetState();

        this.loadGraphData();

        this.$timeout(1000).then(() => {
            this.exporting = false;
        });
    }
}

export class TemperatureGraphComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            vehicle: "<",
            start: "<",
            end: "<",
            legendTopRight: "<",

            onPlotPointHover: "&",
            highlightedPlot: "<",
            plots: "<",

        };

        this.controller = TemperatureGraphComponentController;
        this.template = require("./temperatureGraph.html");
    }
}
